// Qué dice la tarjeta de «Elige el teléfono» sobre el crédito: SureKeep con su enganche, otras financieras o contado.
// Puro, sin React.
import { financierName } from '@/domains/credit/origination/lib/originationMetrics';
import { formatMXN } from '@/shared/lib/format/money';
import { minDownPaymentCents } from './downPaymentBounds';
import { ArrowDown, ArrowUp, CreditCard, Percent } from 'lucide-react';
import { levelLabel, reachesLevel } from './promotionDetail';
import type { BenefitBubble } from './promotionSlots';
import type { CatalogItem, PlanLevel, PromotionEffect, ScopeWidePromotion } from './types';

const SUREKEEP = 'SUREKEEP';

/** Cotas del plan del nivel del cliente que manda el catálogo. */
export interface LevelCreditTerms {
    /** Enganche mínimo del plan, decimal sobre 1. */
    minPct?: number;
    /** Tope de financiamiento del nivel. */
    capCents?: number;
}

/** Enganche mínimo y si lo subió el tope del nivel. */
export interface CardDownPayment {
    cents: number;
    cappedByLevel: boolean;
}

export type CardCredit =
    | { kind: 'surekeep'; downPayment: CardDownPayment | null }
    | { kind: 'others' }
    | { kind: 'cash' };

type FinanceFields = Pick<CatalogItem, 'eligibleFinanciers' | 'financeable' | 'affordableOnCredit'>;

/** Financieras que sí lo financian, en orden alfabético; SureKeep sale si dijo que no. */
export function financiersFor(item: FinanceFields): string[] {
    const sureKeepSaysNo = item.financeable === false || item.affordableOnCredit === false;
    return item.eligibleFinanciers
        .filter((code) => !(sureKeepSaysNo && code === SUREKEEP))
        .sort((a, b) => financierName(a).localeCompare(financierName(b), 'es-MX'));
}

/** SureKeep lo financia: está entre sus financieras y no dijo que no. */
export function sureKeepFinances(item: FinanceFields): boolean {
    return financiersFor(item).includes(SUREKEEP);
}

/** «Solo de contado»: ninguna financiera lo toma, SureKeep incluida. */
export function isCashOnly(item: FinanceFields): boolean {
    return financiersFor(item).length === 0;
}

/** Cotas del catálogo; si faltan arriba, las de la fila del equipo. */
export function creditTermsFor(
    item: Pick<CatalogItem, 'minDownPaymentPct' | 'creditCapCents'>,
    terms: LevelCreditTerms,
): LevelCreditTerms {
    const minPct = terms.minPct ?? item.minDownPaymentPct;
    const capCents = terms.capCents ?? item.creditCapCents;
    return {
        ...(minPct !== undefined && { minPct }),
        ...(capCents !== undefined && { capCents }),
    };
}

/** Enganche desde: máx(% del plan × precio cobrado, precio − tope), sin bajar de 0; `null` sin datos. */
export function downPaymentFrom(
    item: Pick<
        CatalogItem,
        'cashPriceCents' | 'minDownPaymentCents' | 'minDownPaymentPct' | 'creditCapCents'
    >,
    priceCents: number,
    levelTerms: LevelCreditTerms,
): CardDownPayment | null {
    const terms = creditTermsFor(item, levelTerms);
    if (terms.minPct !== undefined) {
        const cents = Math.max(0, minDownPaymentCents(priceCents, terms.minPct, terms.capCents));
        const policy = minDownPaymentCents(priceCents, terms.minPct, undefined);
        return { cents, cappedByLevel: cents > policy };
    }
    if (priceCents === item.cashPriceCents && item.minDownPaymentCents !== undefined) {
        return { cents: item.minDownPaymentCents, cappedByLevel: true };
    }
    return null;
}

/** Sección de crédito de la tarjeta. */
export function cardCreditOf(
    item: FinanceFields &
        Pick<
            CatalogItem,
            'cashPriceCents' | 'minDownPaymentCents' | 'minDownPaymentPct' | 'creditCapCents'
        >,
    priceCents: number,
    terms: LevelCreditTerms,
): CardCredit {
    const financiers = financiersFor(item);
    if (financiers.includes(SUREKEEP)) {
        return { kind: 'surekeep', downPayment: downPaymentFrom(item, priceCents, terms) };
    }
    return financiers.length > 0 ? { kind: 'others' } : { kind: 'cash' };
}

/** De dónde sale el enganche desde, para el detalle; `null` si no hay nada que explicar. */
export function downPaymentNote(
    downPayment: CardDownPayment,
    priceCents: number,
    level: PlanLevel | null | undefined,
    terms: LevelCreditTerms,
): string | null {
    const prefix = level ? `Nivel ${levelLabel(level)}: ` : '';
    if (downPayment.cappedByLevel && terms.capCents !== undefined) {
        return `${prefix}el precio pasa el tope de ${formatMXN(terms.capCents / 100)}, así que el enganche cubre la diferencia.`;
    }
    if (downPayment.cappedByLevel) return `${prefix}el precio pasa el tope del nivel.`;
    if (terms.minPct === undefined) return null;
    return `${prefix}${String(Math.round(terms.minPct * 100))} % de ${formatMXN(priceCents / 100)}.`;
}

const WHOLE_PESOS = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
});

/** Burbujas moradas de las promos de crédito propias del equipo (no las de toda la tienda), en orden alfabético.
 * De cada campo se toma lo mejor y los bonos se suman. */
export function cardCreditBubbles(
    item: Pick<CatalogItem, 'promotions'>,
    scopeWide: readonly Pick<ScopeWidePromotion, 'promotionId'>[],
    clientLevel: PlanLevel | null | undefined,
    /** Sin nivel resuelto: el tooltip aclara que depende del nivel del cliente. */
    levelPending = false,
): BenefitBubble[] {
    const storeIds = new Set(scopeWide.map((promotion) => promotion.promotionId));
    const own = (item.promotions ?? []).filter(
        (p) => !storeIds.has(p.promotionId) && reachesLevel(p.targetLevels, clientLevel),
    );
    const from = (pick: (e: PromotionEffect) => number | null | undefined) => {
        const found = own.flatMap((p) => {
            const value = pick(p.effect);
            return typeof value === 'number' && value !== 0 ? [{ value, name: p.name }] : [];
        });
        return { values: found.map((f) => f.value), title: found.map((f) => f.name).join(' · ') };
    };
    const bubbles: BenefitBubble[] = [];
    const add = (key: string, label: string, title: string, icon: BenefitBubble['icon']): void => {
        const fullTitle = levelPending ? `${title} · Según su nivel` : title;
        bubbles.push({ key: `credit-${key}`, label, icon, title: fullTitle, tone: 'credit' });
    };

    const downPayment = from((e) => e.downPaymentDeltaPct);
    if (downPayment.values.length > 0) {
        const best = Math.min(...downPayment.values);
        const points = String(Math.abs(Math.round(best * 100)));
        add(
            'downPayment',
            `Enganche ${best < 0 ? '−' : '+'}${points} pts`,
            downPayment.title,
            ArrowDown,
        );
    }
    const rate = from((e) => ((e.tnaOverride ?? 0) > 0 ? e.tnaOverride : null));
    if (rate.values.length > 0) {
        add(
            'tna',
            `Tasa ${String(Math.round(Math.min(...rate.values) * 100))}%`,
            rate.title,
            Percent,
        );
    }
    const amount = from((e) =>
        (e.maxAmountOverrideCents ?? 0) > 0 ? e.maxAmountOverrideCents : null,
    );
    if (amount.values.length > 0) {
        add(
            'maxAmount',
            `Hasta ${WHOLE_PESOS.format(Math.max(...amount.values) / 100)}`,
            amount.title,
            ArrowUp,
        );
    }
    const bonus = from((e) => ((e.pcfBonusCents ?? 0) > 0 ? e.pcfBonusCents : null));
    if (bonus.values.length > 0) {
        const total = bonus.values.reduce((sum, n) => sum + n, 0);
        add('bonus', `Bono ${WHOLE_PESOS.format(total / 100)}`, bonus.title, CreditCard);
    }
    return bubbles.sort((a, b) => a.label.localeCompare(b.label, 'es-MX'));
}
